import React from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useMantineColorScheme } from "@mantine/core";
import { ThemeProvider } from "styled-components";
import { NextSeo } from "next-seo";
import { darkTheme, lightTheme } from "../constants/theme";
import useGraph from "../features/editor/views/GraphView/stores/useGraph";
import useFile from "../store/useFile";
import useJson from "../store/useJson";
import { toJpeg, toPng, toSvg } from "html-to-image";

type ExportOptions = {
  extension?: "png" | "jpeg" | "svg";
  filename?: string;
  backgroundColor?: string;
  quality?: number;
};

const downloadURI = (uri: string, name: string) => {
  const link = document.createElement("a");
  link.download = name;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const findGraphElement = (): HTMLElement | null => {
  const selectors = [
    '[data-export-id="jsoncrack-graph-root"] svg',
    ".jsoncrack-canvas svg",
    ".jsoncrack-space svg",
    'svg[id*="ref"]',
    "svg",
  ];

  for (const s of selectors) {
    const el = document.querySelector(s) as HTMLElement | null;
    if (el) return el;
  }
  return null;
};

const exportGraph = async (opts: ExportOptions = {}) => {
  const extension = (opts.extension || "png").toLowerCase();
  const filename = opts.filename || "graph";
  const bg = opts.backgroundColor || "#FFFFFF";
  const quality = typeof opts.quality === "number" ? opts.quality : 1;

  const rootEl = findGraphElement();
  if (!rootEl) throw new Error("Graph element not found");

  if (extension === "svg") {
    const dataURI = await toSvg(rootEl as unknown as HTMLElement);
    downloadURI(dataURI, `${filename}.svg`);
    return;
  }

  const dataURI = await (extension === "jpeg"
    ? toJpeg(rootEl as unknown as HTMLElement, { quality, backgroundColor: bg })
    : toPng(rootEl as unknown as HTMLElement, { quality, backgroundColor: bg }));
  downloadURI(dataURI, `${filename}.${extension}`);
};

const ModalController = dynamic(() => import("../features/modals/ModalController"), {
  ssr: false,
});

const GraphView = dynamic(
  () => import("../features/editor/views/GraphView").then(c => c.GraphView),
  {
    ssr: false,
  }
);

const WidgetPage = () => {
  const { query, push, isReady } = useRouter();
  const { setColorScheme } = useMantineColorScheme();
  const [theme, setTheme] = React.useState<"dark" | "light">("dark");
  const checkEditorSession = useFile(state => state.checkEditorSession);
  const clearGraph = useGraph(state => state.clearGraph);
  const setLoading = useGraph(state => state.setLoading);
  const setJson = useJson(state => state.setJson);

  React.useEffect(() => {
    if (isReady) {
      if (typeof query?.json === "string") checkEditorSession(query.json, true);
      else clearGraph();

      window.parent.postMessage(window.frameElement?.getAttribute("id"), "*");
    }
  }, [clearGraph, checkEditorSession, isReady, push, query.json, query.partner]);

  React.useEffect(() => {
    const handler = async (event: MessageEvent) => {
      try {
        if (event.data && event.data.action === "export-graph") {
          try {
            await exportGraph(event.data.options || {});
            window.parent.postMessage({ action: "export-complete", status: "ok" }, "*");
          } catch (err: unknown) {
            const errMsg = err instanceof Error ? err.message : String(err);
            window.parent.postMessage(
              { action: "export-complete", status: "error", message: errMsg },
              "*"
            );
          }
          return;
        }

        if (!event.data?.json) return;

        const jsonData = event.data.json;

        if (event.data?.options?.theme === "light" || event.data?.options?.theme === "dark") {
          setTheme(event.data.options.theme);
        }

        setLoading(true);
        clearGraph();

        setTimeout(() => {
          setJson(jsonData);
        }, 100);
      } catch (error) {
        console.error("Widget JSON processing error:", error);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [clearGraph, setJson, setLoading]);

  React.useEffect(() => {
    setColorScheme(theme);
  }, [setColorScheme, theme]);

  return (
    <div style={{ backgroundColor: "#0f0f0f", minHeight: "100vh", width: "100%" }}>
      <ThemeProvider theme={theme === "dark" ? darkTheme : lightTheme}>
        <NextSeo noindex nofollow />
        <ModalController />
        <GraphView isWidget />
      </ThemeProvider>
    </div>
  );
};

export default WidgetPage;
